import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import logo from '../assets/logo_leela.jpg';
import { chips } from '@/utils/chips.js';

const createConnectionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `conn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const CONNECTION_ERROR_CODES = new Set(['ALREADY_CONNECTED', 'SESSION_REPLACED', 'NOT_ACTIVE_CONNECTION']);

const GameInvitePage = () => {
  const { roomId } = useParams();
  const { t } = useTranslation(['dashboard', 'common']);
  const { navigate } = useLanguageNavigation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token') || '';
  const playerId = Number(searchParams.get('player')) || null;

  const storageKey = useMemo(
    () => `game-session-${roomId}-player-${playerId || 'any'}-token`,
    [playerId, roomId],
  );
  const connectionStorageKey = useMemo(
    () => `game-session-${roomId}-connection`,
    [roomId],
  );

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [alreadyConnectedInfo, setAlreadyConnectedInfo] = useState(null);
  const [retryCountdown, setRetryCountdown] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [playerChipKey, setPlayerChipKey] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const redirectingRef = useRef(false);
  const forceStateRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const [errorMode, setErrorMode] = useState('form');
  const [stateAttempt, setStateAttempt] = useState(0);
  const [connectionId] = useState(() => {
    try {
      const storedConnection = sessionStorage.getItem(connectionStorageKey) || '';
      if (storedConnection) {
        return storedConnection;
      }
      const nextConnectionId = createConnectionId();
      sessionStorage.setItem(connectionStorageKey, nextConnectionId);
      return nextConnectionId;
    } catch (storageError) {
      console.error('[GameInviteDebug] connection storage error', storageError);
      return '';
    }
  });

  console.debug('[GameInviteDebug] init', {
    roomId,
    inviteToken,
    playerId,
    storageKey,
    initialStatus: status,
  });

  const takenChipKeys = useMemo(() => {
    if (!Array.isArray(sessionState?.players)) {
      return new Set();
    }
    return new Set(
      sessionState.players
        .filter((player) => player.id !== playerId)
        .map((player) => player.chipKey)
        .filter(Boolean),
    );
  }, [playerId, sessionState?.players]);

  const chipChoices = useMemo(
    () =>
      chips.map((chip) => ({
        ...chip,
        disabled: takenChipKeys.has(chip.chipKey),
      })),
    [takenChipKeys],
  );

  useEffect(() => {
    redirectingRef.current = redirecting;
  }, [redirecting]);

  const triggerStateFetch = useCallback((force = false) => {
    forceStateRef.current = force;
    setStateAttempt((prev) => prev + 1);
  }, []);

  const redirectToGame = useCallback(
    (sessionData, participantData) => {
      console.debug('[GameInviteDebug] redirectToGame called', {
        redirecting: redirectingRef.current,
        hasSession: Boolean(sessionData),
        gameId: sessionData?.gameId,
        hasParticipant: Boolean(participantData),
        playerId: participantData?.playerId,
      });

      if (redirectingRef.current || !sessionData?.gameId || !participantData) {
        console.debug('[GameInviteDebug] redirectToGame skipped', {
          reason: redirectingRef.current
            ? 'already redirecting'
            : !sessionData?.gameId
              ? 'no gameId'
              : 'no participant',
        });
        return false;
      }

      const params = new URLSearchParams();
      if (participantData.playerId) {
        params.set('player', participantData.playerId);
      }
      if (inviteToken) {
        params.set('token', inviteToken);
      }
      if (sessionData.roomId) {
        params.set('room', sessionData.roomId);
      }

      console.debug('[GameInviteDebug] redirectToGame navigating', {
        path: `/room/${sessionData.gameId}`,
        query: params.toString(),
      });

      setStatus('loading');
      setRedirecting(true);
      redirectingRef.current = true;

      navigate(`/room/${sessionData.gameId}${params.toString() ? `?${params.toString()}` : ''}`, {
        replace: true,
      });

      return true;
    },
    [inviteToken, navigate],
  );

  const applySessionResponse = useCallback(
    (data) => {
      console.debug('[GameInviteDebug] applySessionResponse input', {
        raw: data,
      });

      const sessionData = data?.session || null;
      const participantData = data?.participant || null;
      const participantToken = data?.playerToken || participantData?.clientToken || '';

      setSession(sessionData);
      setParticipant(participantToken ? participantData : null);
      setSessionState(data?.state || null);
      setAlreadyConnectedInfo(null);
      setRetryCountdown(null);

      if (!sessionData) {
        console.debug('[GameInviteDebug] applySessionResponse no sessionData');
        setError(t('gameInvite.messages.sessionNotFound'));
        setErrorMode('retry');
        setStatus('error');
        return { redirected: false, blocked: true, sessionData, participantData };
      }

      const redirected = participantToken ? redirectToGame(sessionData, participantData) : false;

      if (!redirected) {
        const nextStatus = participantToken ? 'ready' : 'form';
        console.debug('[GameInviteDebug] applySessionResponse no redirect, set status', {
          nextStatus,
          hasParticipant: Boolean(participantData && participantToken),
        });
        setError('');
        setErrorMode('form');
        setStatus(nextStatus);
      } else {
        console.debug('[GameInviteDebug] applySessionResponse redirected');
      }

      return { redirected, blocked: false, sessionData, participantData };
    },
    [redirectToGame, t],
  );

  const joinSession = useCallback(
    async (payload = {}) => {
      console.debug('[GameInviteDebug] joinSession called', {
        roomId,
        inviteToken,
        playerId,
        payload,
      });

      if (!inviteToken) {
        setError(t('gameInvite.messages.missingToken'));
        setStatus('error');
        return;
      }

      setStatus('loading');
      const storedToken = localStorage.getItem(storageKey) || '';

      try {
        const nextConnectionId = connectionId;

        const body = {
          token: inviteToken,
          participantToken: payload.participantToken || storedToken || undefined,
          connectionId: nextConnectionId || undefined,
        };

        if (playerId) {
          body.playerId = playerId;
          body.playerName = payload.playerName;
          body.playerChipKey = payload.playerChipKey;
        }

        const response = await fetch(apiUrl(`/api/game-sessions/${roomId}/join`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorCode = errorData?.error || errorData?.code || '';
          if (CONNECTION_ERROR_CODES.has(errorCode)) {
            setAlreadyConnectedInfo(errorData);
            setError('');
            setErrorMode('alreadyConnected');
            setStatus('error');
            return;
          }
          if (errorData?.error === 'CHIP_TAKEN') {
            setPlayerChipKey('');
            throw new Error(t('gameInvite.messages.chipTaken'));
          }
          throw new Error(errorData.error || t('gameInvite.messages.joinFailed'));
        }
        const data = await response.json();
        console.debug('[GameInviteDebug] joinSession success', {
          sessionId: data?.session?.id,
          gameId: data?.session?.gameId,
          hasParticipant: Boolean(data?.participant),
        });
        const clientToken = data?.playerToken || data?.participant?.clientToken || '';
        if (clientToken) {
          localStorage.setItem(storageKey, clientToken);
        }
        applySessionResponse(data);
      } catch (joinError) {
        console.error('[GameInviteDebug] joinSession error', joinError);
        setError(joinError.message || t('gameInvite.messages.joinFailed'));
        setErrorMode('form');
        setStatus('form');
      }
    },
    [
      applySessionResponse,
      connectionId,
      inviteToken,
      playerId,
      roomId,
      storageKey,
      t,
    ],
  );

  const sendDisconnect = useCallback(() => {
    if (!roomId || !inviteToken || !connectionId) {
      return;
    }
    const storedToken = localStorage.getItem(storageKey) || '';
    if (!storedToken) {
      return;
    }
    const payload = {
      token: inviteToken,
      participantToken: storedToken,
      connectionId,
    };
    const payloadText = JSON.stringify(payload);
    const url = apiUrl(`/api/game-sessions/${roomId}/disconnect`);

    if (navigator.sendBeacon) {
      const blob = new Blob([payloadText], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payloadText,
      keepalive: true,
    }).catch((disconnectError) => {
      console.warn('[GameInviteDebug] disconnect failed', disconnectError);
    });
  }, [connectionId, inviteToken, roomId, storageKey]);

  useEffect(() => {
    if (!roomId || !inviteToken) {
      setError(t('gameInvite.messages.missingToken'));
      setErrorMode('retry');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setError('');
    setErrorMode('retry');
    setAlreadyConnectedInfo(null);
    setRetryCountdown(null);

    const controller = new AbortController();
    let didTimeout = false;
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, 15000);

    console.debug('[GameInviteDebug] useEffect state fetch start', {
      roomId,
      inviteToken,
    });

    const storedToken = localStorage.getItem(storageKey) || '';
    const forceTakeover = forceStateRef.current;
    forceStateRef.current = false;
    const queryParts = [
      `token=${inviteToken}`,
      playerId ? `player=${playerId}` : null,
      storedToken ? `participantToken=${storedToken}` : null,
      connectionId ? `connectionId=${connectionId}` : null,
      forceTakeover ? 'force=1' : null,
    ].filter(Boolean);

    fetch(apiUrl(`/api/game-sessions/${roomId}/state?${queryParts.join('&')}`), {
      method: 'GET',
      signal: controller.signal,
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) {
            const errorMessage = typeof data?.error === 'string' ? data.error : '';
            const error = new Error(errorMessage || 'STATE_LOAD_FAILED');
            error.status = response.status;
            error.code = data?.code || data?.error || '';
            error.payload = data;
            throw error;
          }
          return data;
        }),
      )
      .then((data) => {
        if (typeof data?.error === 'string' && data.error.trim()) {
          throw new Error(data.error.trim());
        }
        console.debug('[GameInviteDebug] state fetch success', {
          sessionId: data?.session?.id,
          gameId: data?.session?.gameId,
          hasParticipant: Boolean(data?.participant),
        });
        const resolvedPlayerToken = data?.playerToken || data?.participant?.clientToken || '';
        if (resolvedPlayerToken) {
          localStorage.setItem(storageKey, resolvedPlayerToken);
        }
        applySessionResponse(data);
      })
      .catch((stateError) => {
        if (stateError.name === 'AbortError' && !didTimeout) {
          return;
        }
        console.error('[GameInviteDebug] state fetch error', stateError);
        if (stateError?.status === 409 && CONNECTION_ERROR_CODES.has(stateError?.code)) {
          setAlreadyConnectedInfo(stateError.payload || null);
          setError('');
          setErrorMode('alreadyConnected');
          setStatus('error');
          return;
        }
        if (stateError.name === 'AbortError' && didTimeout) {
          setError(t('gameInvite.messages.loadError'));
        } else if (stateError.message === 'STATE_LOAD_FAILED') {
          setError(t('gameInvite.messages.loadError'));
        } else {
          setError(stateError.message || t('gameInvite.messages.loadError'));
        }
        setErrorMode('retry');
        setStatus('error');
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    applySessionResponse,
    connectionId,
    inviteToken,
    playerId,
    roomId,
    stateAttempt,
    storageKey,
    t,
  ]);

  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }

    if (status !== 'error' || errorMode !== 'alreadyConnected') {
      setRetryCountdown(null);
      return undefined;
    }

    const retryAfterMs = alreadyConnectedInfo?.retryAfterMs;
    if (!retryAfterMs || retryAfterMs <= 0) {
      setRetryCountdown(null);
      return undefined;
    }

    const deadline = Date.now() + retryAfterMs;
    const updateCountdown = () => {
      const remainingMs = Math.max(deadline - Date.now(), 0);
      const nextSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
      setRetryCountdown(nextSeconds || null);
    };

    updateCountdown();
    retryIntervalRef.current = setInterval(updateCountdown, 1000);
    retryTimeoutRef.current = setTimeout(() => {
      triggerStateFetch(false);
    }, retryAfterMs);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, [alreadyConnectedInfo?.retryAfterMs, errorMode, status, triggerStateFetch]);

  useEffect(() => {
    const handleDisconnect = () => {
      sendDisconnect();
    };

    window.addEventListener('pagehide', handleDisconnect);
    window.addEventListener('beforeunload', handleDisconnect);

    return () => {
      window.removeEventListener('pagehide', handleDisconnect);
      window.removeEventListener('beforeunload', handleDisconnect);
    };
  }, [sendDisconnect]);

  useEffect(() => {
    console.debug('[GameInviteDebug] status effect', {
      status,
      redirecting,
      hasSession: Boolean(session),
      hasParticipant: Boolean(participant),
    });

    if (status === 'ready' && session && participant && !redirecting) {
      console.debug('[GameInviteDebug] status effect triggering redirectToGame');
      redirectToGame(session, participant);
    }
  }, [participant, redirectToGame, redirecting, session, status]);

  const playerSlot = useMemo(() => {
    if (!playerId || !Array.isArray(sessionState?.players)) {
      return null;
    }
    return sessionState.players.find((player) => player.id === playerId) || null;
  }, [playerId, sessionState?.players]);

  const normalizedNameMode = useMemo(() => {
    const mode = playerSlot?.nameMode || '';
    if (mode === 'preset') {
      return 'preset';
    }
    if (mode === 'playerInput' || mode === 'input') {
      return 'input';
    }
    return 'input';
  }, [playerSlot?.nameMode]);

  const resolvedPlayerName = useMemo(() => playerSlot?.name?.trim?.() || '', [playerSlot?.name]);

  useEffect(() => {
    if (normalizedNameMode === 'preset') {
      if (playerName !== resolvedPlayerName) {
        setPlayerName(resolvedPlayerName);
      }
    } else if (!playerName && resolvedPlayerName) {
      setPlayerName(resolvedPlayerName);
    }
  }, [normalizedNameMode, playerName, resolvedPlayerName]);

  useEffect(() => {
    const slotChipKey = playerSlot?.chipKey || '';
    if (slotChipKey && slotChipKey !== playerChipKey) {
      setPlayerChipKey(slotChipKey);
      return;
    }
    if (playerChipKey && !chipChoices.some((chip) => chip.chipKey === playerChipKey)) {
      setPlayerChipKey('');
    }
  }, [chipChoices, playerChipKey, playerSlot?.chipKey]);

  useEffect(() => {
    if (!playerChipKey) {
      return;
    }
    if (takenChipKeys.has(playerChipKey)) {
      setError(t('gameInvite.messages.chipTaken'));
      setPlayerChipKey('');
    }
  }, [playerChipKey, t, takenChipKeys]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedName = normalizedNameMode === 'preset' ? resolvedPlayerName : playerName.trim();

    if (normalizedNameMode === 'input') {
      if (!trimmedName) {
        setError(t('gameInvite.form.nameRequired'));
        return;
      }
      if (trimmedName.length < 2 || trimmedName.length > 60) {
        setError(t('gameInvite.form.nameLength'));
        return;
      }
    }

    if (!playerChipKey) {
      setError(t('gameInvite.form.tokenRequired'));
      return;
    }

    await joinSession({ playerName: trimmedName, playerChipKey });
  };

  if (status === 'loading' || redirecting) {
    return (
      <div className="page-loader">
        <p>{t('gameInvite.statusText.loading')}</p>
      </div>
    );
  }

  const showNameInput = normalizedNameMode === 'input';
  const displayedName = resolvedPlayerName || t('gameInvite.form.namePlaceholder');
  const shouldShowForm = status === 'form' || (status === 'error' && errorMode === 'form');
  const shouldShowRetry = status === 'error' && errorMode === 'retry';
  const shouldShowAlreadyConnected = status === 'error' && errorMode === 'alreadyConnected';
  const resolvedTitle = shouldShowAlreadyConnected
    ? t('gameInvite.messages.alreadyConnectedTitle')
    : t('gameInvite.heading.title');

  return (
    <div className="public-page">
      <div className="public-card">
        <div className="public-card__header">
          <img src={logo} alt={t('platform.caption', { ns: 'common' })} className="invite-logo" />
          <h1 className="public-card__title">{resolvedTitle}</h1>
        </div>
        <div className="public-card__body">
          {shouldShowForm && (
            <form className="invite-form" onSubmit={handleFormSubmit}>
              {showNameInput ? (
                <label className="form-field">
                  <span>{t('gameInvite.form.name')}</span>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(event) => setPlayerName(event.target.value)}
                    placeholder={t('gameInvite.form.namePlaceholder')}
                    required
                  />
                </label>
              ) : (
                <div className="form-field">
                  <span className="public-info">
                    {t('gameInvite.form.name')}:
                    <span className="public-info__value">{displayedName}</span>
                  </span>
                </div>
              )}

              <label className="form-field chip-field">
                <span>{t('gameInvite.form.token')}</span>
                <div className="chip-grid">
                  {chipChoices.map((chip) => (
                    <button
                      key={chip.chipKey}
                      type="button"
                      className={`chip-option${playerChipKey === chip.chipKey ? ' selected' : ''}${
                        chip.disabled ? ' disabled' : ''
                      }`}
                      disabled={chip.disabled}
                      onClick={
                        chip.disabled
                          ? undefined
                          : (event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setPlayerChipKey(chip.chipKey);
                            }
                      }
                    >
                      <img className="chip-preview" src={chip.src} alt="" />
                    </button>
                  ))}
                </div>
              </label>
              <button className="primary-button" type="submit">
                {t('gameInvite.form.submit')}
              </button>
              {error && <p className="form-error">{error}</p>}
            </form>
          )}

          {shouldShowRetry && (
            <div className="game-invite-error">
              <p>{error || t('gameInvite.messages.loadError')}</p>
              <button
                type="button"
                className="primary-button"
                onClick={() => triggerStateFetch(false)}
              >
                {t('gameInvite.actions.retry')}
              </button>
            </div>
          )}

          {shouldShowAlreadyConnected && (
            <div className="game-invite-error">
              <p>{t('gameInvite.messages.alreadyConnectedBody')}</p>
              {retryCountdown ? (
                <p className="game-invite-retry-hint">
                  {t('gameInvite.messages.alreadyConnectedRetryHint', { seconds: retryCountdown })}
                </p>
              ) : null}
              <div className="game-invite-actions">
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => triggerStateFetch(true)}
                >
                  {t('gameInvite.actions.reconnect')}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => triggerStateFetch(false)}
                >
                  {t('gameInvite.actions.retry')}
                </button>
              </div>
            </div>
          )}

          {status === 'ready' && !redirecting && participant && (
            <div className="game-invite-ready">
              <p>{t('gameInvite.statusText.ready')}</p>
              <button
                type="button"
                className="primary-button"
                onClick={() => redirectToGame(session, participant)}
              >
                {t('gameInvite.actions.goToGame')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameInvitePage;
